import React, { useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, FileSpreadsheet, Loader2, Upload, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';

/**
 * A row as returned by POST /assets/import/validate: the resolved asset
 * fields, plus the original cell values under `_raw` and the backend's verdict.
 */
export interface AssetImportPreviewRow {
    _row: number;
    _raw?: Record<string, any>;
    _status: 'READY' | 'WARNING' | 'ERROR';
    _errors: string[];
    /**
     * The subset of `_errors` the backend attributes to the Asset Tag. Editing
     * the tag retracts exactly these and re-derives them locally, so the other
     * errors on the row are never disturbed.
     */
    _assetTagErrors?: string[];
    _warnings: string[];
    _isValid: boolean;
    [key: string]: any;
}

export interface AssetImportPreviewSummary {
    total: number;
    ready: number;
    warning: number;
    invalid: number;
}

interface AssetImportPreviewProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    rows: AssetImportPreviewRow[];
    summary?: AssetImportPreviewSummary;
    isImporting: boolean;
    onConfirm: () => void;
    /** Applies an Asset Tag edit. Omit to render the tag read-only. */
    onRowsChange?: (rows: AssetImportPreviewRow[]) => void;
    /**
     * Normalized Asset Tags the backend already found taken in the database,
     * so re-typing one is flagged without another round trip. A tag not in
     * this list is still checked on the server at Confirm Import.
     */
    takenAssetTagKeys?: string[];
}

/** Mirrors ASSET_TAG_MIN_LENGTH / ASSET_FIELD_MAX_LENGTHS.assetTag on the backend. */
const ASSET_TAG_MIN_LENGTH = 3;
const ASSET_TAG_MAX_LENGTH = 100;

/** Mirrors normalizeAssetTag() in asset-duplicate.util.ts: trim + case-fold. */
const normalizeAssetTag = (value: unknown): string =>
    String(value ?? '').trim().toLowerCase();

/**
 * These reproduce the backend's wording verbatim (asset-duplicate.util.ts and
 * resolveImportRow) so a locally re-derived message is indistinguishable from
 * the one the server sent for the same problem.
 */
const tagMessages = {
    tooShort: (rowNumber: number, tag: string) =>
        `Row ${rowNumber}: Asset Tag "${tag}" is too short — the minimum is ${ASSET_TAG_MIN_LENGTH} characters`,
    tooLong: (rowNumber: number, tag: string) =>
        `Row ${rowNumber}: Asset Tag is ${tag.length} characters — the maximum is ${ASSET_TAG_MAX_LENGTH}`,
    duplicateOfRow: (otherRowNumber: number, tag: string) =>
        `Asset Tag "${tag}" is already used in Excel row ${otherRowNumber}.`,
    duplicateOfExisting: (tag: string) =>
        `Asset Tag "${tag}" is already assigned to another asset.`,
};

/**
 * Re-derives every row's Asset Tag errors and status after an edit, applying
 * the same rules the backend does: length bounds, then a case-insensitive
 * duplicate check against earlier rows and against the tags already known to
 * be taken. Rows that are invalid for some *other* reason are not candidates
 * to own a tag, matching the backend, which skips them entirely.
 *
 * Exported for testing, and pure — it returns new row objects rather than
 * mutating the ones handed to it.
 */
export function recomputeAssetTagState(
    rows: AssetImportPreviewRow[],
    takenAssetTagKeys: Iterable<string> = [],
): AssetImportPreviewRow[] {
    const taken = new Set(Array.from(takenAssetTagKeys, normalizeAssetTag));
    const seen = new Map<string, number>();

    return rows.map((row) => {
        const tag = String(row.assetTag ?? '').trim();
        // `_assetTagErrors` is authoritative; the text match is only a
        // fallback for a row that reached us without it (an older backend),
        // so a stale server message can't survive an edit and double up.
        const previousTagErrors =
            row._assetTagErrors ?? (row._errors ?? []).filter((e) => e.includes('Asset Tag'));
        const otherErrors = (row._errors ?? []).filter((e) => !previousTagErrors.includes(e));
        const tagErrors: string[] = [];

        if (tag && tag.length < ASSET_TAG_MIN_LENGTH) {
            tagErrors.push(tagMessages.tooShort(row._row, tag));
        } else if (tag.length > ASSET_TAG_MAX_LENGTH) {
            tagErrors.push(tagMessages.tooLong(row._row, tag));
        } else if (tag && otherErrors.length === 0) {
            const key = normalizeAssetTag(tag);
            const priorRow = seen.get(key);
            if (priorRow !== undefined) {
                tagErrors.push(tagMessages.duplicateOfRow(priorRow, tag));
            } else if (taken.has(key)) {
                tagErrors.push(tagMessages.duplicateOfExisting(tag));
            } else {
                seen.set(key, row._row);
            }
        }

        const errors = [...otherErrors, ...tagErrors];
        const warnings = row._warnings ?? [];
        return {
            ...row,
            assetTag: tag || '',
            _errors: errors,
            _assetTagErrors: tagErrors,
            _status: (errors.length > 0 ? 'ERROR' : warnings.length > 0 ? 'WARNING' : 'READY') as AssetImportPreviewRow['_status'],
            _isValid: errors.length === 0,
        };
    });
}

const text = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
};

/** Every column of the import template, in template order. */
const FIELDS: { key: string; label: string; value: (row: AssetImportPreviewRow) => string; mono?: boolean; editable?: boolean }[] = [
    {
        key: 'assetTag',
        label: 'Asset Tag',
        // Blank is a valid choice, not missing data, so it reads as the
        // outcome it will produce rather than as an empty cell.
        value: (r) => String(r.assetTag ?? r._raw?.assetTag ?? '').trim() || 'Auto-generated during import',
        mono: true,
        editable: true,
    },
    { key: 'name', label: 'Name', value: (r) => text(r.name ?? r._raw?.name) },
    { key: 'category', label: 'Category', value: (r) => text(r.category ?? r._raw?.category) },
    { key: 'brand', label: 'Brand', value: (r) => text(r.brand ?? r._raw?.brand) },
    { key: 'model', label: 'Model', value: (r) => text(r.model ?? r._raw?.model) },
    { key: 'serialNumber', label: 'Serial Number', value: (r) => text(r.serialNumber ?? r._raw?.serialNumber), mono: true },
    { key: 'status', label: 'Status', value: (r) => text(r._raw?.status ?? r.status) },
    { key: 'condition', label: 'Condition', value: (r) => text(r._raw?.condition ?? r.condition) },
    { key: 'acquisitionType', label: 'Acquisition Type', value: (r) => text(r.acquisitionType ?? r._raw?.acquisitionType) },
    { key: 'purchaseDate', label: 'Purchase Date', value: (r) => text(r.purchaseDate ?? r._raw?.purchaseDate) },
    { key: 'purchaseCost', label: 'Purchase Cost', value: (r) => text(r.purchaseCost ?? r._raw?.purchaseCost) },
    { key: 'vendorMonthlyRent', label: 'Vendor Monthly Rent', value: (r) => text(r.vendorMonthlyRent ?? r._raw?.vendorMonthlyRent) },
    { key: 'receivedFromVendorDate', label: 'Rental Start Date', value: (r) => text(r.receivedFromVendorDate ?? r._raw?.receivedFromVendorDate) },
    { key: 'currency', label: 'Currency', value: (r) => text(r.currency ?? r._raw?.currency) },
    { key: 'vendor', label: 'Vendor', value: (r) => text(r.vendor ?? r._raw?.vendor) },
    { key: 'warrantyExpiry', label: 'Warranty Expiry', value: (r) => text(r.warrantyExpiry ?? r._raw?.warrantyExpiry) },
    { key: 'notes', label: 'Notes', value: (r) => text(r.notes ?? r._raw?.notes) },
    { key: 'hostname', label: 'Hostname', value: (r) => text(r.hostname ?? r._raw?.hostname) },
    { key: 'poNumber', label: 'PO Number', value: (r) => text(r.poNumber ?? r._raw?.poNumber), mono: true },
    { key: 'invoiceNumber', label: 'Invoice Number', value: (r) => text(r.invoiceNumber ?? r._raw?.invoiceNumber), mono: true },
    { key: 'costCenter', label: 'Cost Center', value: (r) => text(r.costCenter ?? r._raw?.costCenter) },
    { key: 'usefulLifeYears', label: 'Useful Life Years', value: (r) => text(r.usefulLifeYears ?? r._raw?.usefulLifeYears) },
];

/**
 * Row tints, in two forms. A sticky column has the rest of the table scrolling
 * underneath it, so its background must be fully opaque — an alpha utility like
 * `bg-destructive/10` lets the columns below show straight through and the text
 * collides. The sticky variant therefore paints the tint as a background-image
 * layer over an opaque background-color, which the alpha utilities cannot do
 * (both would set background-color and the last one would win).
 */
const TONES = {
    ERROR: { cell: 'bg-destructive/10', sticky: 'sticky-cell-error' },
    WARNING: { cell: 'bg-warning/10', sticky: 'sticky-cell-warning' },
    READY: { cell: 'bg-background', sticky: 'sticky-cell' },
} as const;

const STICKY_LEFT_EDGE = 'shadow-[1px_0_0_0_hsl(var(--border))]';
const STICKY_RIGHT_EDGE = 'shadow-[-1px_0_0_0_hsl(var(--border))]';

/** The subset shown as columns in the collapsed desktop table. */
const SUMMARY_KEYS = ['assetTag', 'name', 'category', 'brand', 'model', 'serialNumber', 'acquisitionType', 'purchaseDate', 'vendor'];
const SUMMARY_FIELDS = SUMMARY_KEYS.map((key) => FIELDS.find((f) => f.key === key)!);
const fieldByKey = (key: string) => FIELDS.find((f) => f.key === key)!;

/**
 * The one editable cell in the review screen. Blank is a valid, deliberate
 * state — the placeholder says so rather than the field looking unfinished —
 * and the tag is committed on change so the row's status updates as it is
 * typed, the same way the backend will judge it on confirm.
 */
const AssetTagInput: React.FC<{
    row: AssetImportPreviewRow;
    onChange: (value: string) => void;
    className?: string;
}> = ({ row, onChange, className }) => (
    <input
        type="text"
        value={row.assetTag ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Auto-generated during import"
        aria-label={`Asset Tag for row ${row._row}`}
        aria-invalid={(row._assetTagErrors?.length ?? 0) > 0 || undefined}
        className={cn(
            'h-9 w-full min-w-[200px] rounded-md border border-input bg-background px-2 font-mono text-xs',
            'placeholder:font-sans placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            (row._assetTagErrors?.length ?? 0) > 0 && 'border-destructive',
            className,
        )}
    />
);

const StatusBadge: React.FC<{ row: AssetImportPreviewRow }> = ({ row }) => {
    if (row._status === 'ERROR') {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive whitespace-nowrap">
                <X className="h-3 w-3" /> Invalid
            </span>
        );
    }
    if (row._status === 'WARNING') {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning whitespace-nowrap">
                <AlertTriangle className="h-3 w-3" /> Warning
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success whitespace-nowrap">
            <Check className="h-3 w-3" /> Ready
        </span>
    );
};

const Messages: React.FC<{ row: AssetImportPreviewRow }> = ({ row }) => {
    const errors = row._errors ?? [];
    const warnings = row._warnings ?? [];
    if (errors.length === 0 && warnings.length === 0) return null;
    return (
        <ul className="space-y-1">
            {errors.map((message, i) => (
                <li key={`e${i}`} className="flex items-start gap-1.5 text-xs text-destructive">
                    <X className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{message}</span>
                </li>
            ))}
            {warnings.map((message, i) => (
                <li key={`w${i}`} className="flex items-start gap-1.5 text-xs text-warning">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{message}</span>
                </li>
            ))}
        </ul>
    );
};

const RowDetails: React.FC<{ row: AssetImportPreviewRow }> = ({ row }) => (
    <div className="space-y-3 bg-muted/30 p-3 sm:p-4">
        <Messages row={row} />
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
            {FIELDS.map((field) => (
                <div key={field.key} className="flex flex-col border-b border-border/50 py-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
                    <dt className="text-xs text-muted-foreground">{field.label}</dt>
                    <dd className={cn('break-words text-sm sm:text-right', field.mono && 'font-mono text-xs')}>{field.value(row)}</dd>
                </div>
            ))}
        </dl>
    </div>
);

const AssetImportPreview: React.FC<AssetImportPreviewProps> = ({ open, onOpenChange, rows, summary, isImporting, onConfirm, onRowsChange, takenAssetTagKeys }) => {
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    const editAssetTag = (rowNumber: number, value: string) => {
        if (!onRowsChange) return;
        onRowsChange(
            recomputeAssetTagState(
                rows.map((r) => (r._row === rowNumber ? { ...r, assetTag: value } : r)),
                takenAssetTagKeys,
            ),
        );
    };

    // Rendered in both the mobile card list and the desktop table, so the two
    // breakpoint trees each hold one input per row.
    const tagInput = (row: AssetImportPreviewRow) =>
        onRowsChange ? <AssetTagInput row={row} onChange={(value) => editAssetTag(row._row, value)} /> : null;

    const toggle = (rowNumber: number) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(rowNumber)) next.delete(rowNumber);
            else next.add(rowNumber);
            return next;
        });

    // The backend sends a summary; recomputing locally keeps the dialog correct
    // against an older backend that does not yet return one — and once Asset
    // Tags can be edited here, the server's counts go stale on the first edit,
    // so the live rows win whenever editing is enabled.
    const counts = useMemo<AssetImportPreviewSummary>(() => {
        const local = {
            total: rows.length,
            ready: rows.filter((r) => r._status === 'READY').length,
            warning: rows.filter((r) => r._status === 'WARNING').length,
            invalid: rows.filter((r) => r._status === 'ERROR').length,
        };
        return onRowsChange ? local : summary ?? local;
    }, [rows, summary, onRowsChange]);

    const importableCount = counts.ready + counts.warning;

    const chips: { label: string; count: number; className: string }[] = [
        { label: 'Total', count: counts.total, className: 'bg-muted text-foreground' },
        { label: 'Ready', count: counts.ready, className: 'bg-success/10 text-success' },
        { label: 'Warning', count: counts.warning, className: 'bg-warning/10 text-warning' },
        { label: 'Invalid', count: counts.invalid, className: 'bg-destructive/10 text-destructive' },
    ];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex flex-col sm:max-w-6xl sm:max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-5 w-5 text-primary" />
                        Review Asset Import Data
                    </DialogTitle>
                </DialogHeader>

                <div className="flex flex-wrap gap-2">
                    {chips.map((chip) => (
                        <span key={chip.label} className={cn('rounded-full px-2.5 py-1 text-xs font-medium', chip.className)}>
                            {chip.label}: {chip.count}
                        </span>
                    ))}
                </div>

                {onRowsChange && (
                    <p className="text-xs text-muted-foreground">
                        Asset Tag can be edited here. Leave it blank to have one generated during import.
                    </p>
                )}

                {counts.invalid > 0 && (
                    <p className="text-xs text-muted-foreground">
                        Invalid rows are excluded — the rest still import. Expand a row to see every imported value.
                    </p>
                )}

                {/* Mobile: card list. Each card expands to the full 22 fields. */}
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto sm:hidden">
                    {rows.map((row) => (
                        <div key={row._row} className={cn('overflow-hidden rounded-md border', row._status === 'ERROR' && 'border-destructive/40 bg-destructive/5')}>
                            <button
                                type="button"
                                onClick={() => toggle(row._row)}
                                aria-expanded={expanded.has(row._row)}
                                className="flex w-full items-center justify-between gap-2 p-3 text-left"
                            >
                                <div className="min-w-0">
                                    <div className="truncate text-sm font-medium">{fieldByKey('name').value(row)}</div>
                                    <div className="truncate text-xs text-muted-foreground">
                                        Row {row._row} · {fieldByKey('category').value(row)}
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    <StatusBadge row={row} />
                                    {expanded.has(row._row) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </div>
                            </button>
                            {onRowsChange && (
                                <div className="space-y-1 px-3 pb-3">
                                    <div className="text-xs text-muted-foreground">Asset Tag</div>
                                    {tagInput(row)}
                                </div>
                            )}
                            {expanded.has(row._row) && <RowDetails row={row} />}
                        </div>
                    ))}
                </div>

                {/* Desktop: horizontally scrollable table with sticky Row and Status columns. */}
                <div className="hidden min-h-0 flex-1 overflow-auto rounded-md border sm:block">
                    <table className="w-full min-w-max border-collapse text-sm">
                        <thead className="sticky top-0 z-20 bg-muted">
                            <tr>
                                <th className={cn('sticky left-0 z-30 border-b bg-muted p-2 text-left font-medium', STICKY_LEFT_EDGE)}>Row</th>
                                {SUMMARY_FIELDS.map((field) => (
                                    <th key={field.key} className="whitespace-nowrap border-b bg-muted p-2 text-left font-medium">
                                        {field.label}
                                    </th>
                                ))}
                                <th className={cn('sticky right-0 z-30 border-b bg-muted p-2 text-left font-medium', STICKY_RIGHT_EDGE)}>Validation</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const isOpen = expanded.has(row._row);
                                const tone = TONES[row._status] ?? TONES.READY;
                                return (
                                    <React.Fragment key={row._row}>
                                        <tr className={tone.cell}>
                                            <td className={cn('sticky left-0 z-10 border-b p-2', tone.sticky, STICKY_LEFT_EDGE)}>
                                                <button
                                                    type="button"
                                                    onClick={() => toggle(row._row)}
                                                    aria-expanded={isOpen}
                                                    aria-label={`${isOpen ? 'Hide' : 'Show'} all fields for row ${row._row}`}
                                                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                                                >
                                                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                    {row._row}
                                                </button>
                                            </td>
                                            {SUMMARY_FIELDS.map((field) =>
                                                field.editable && onRowsChange ? (
                                                    <td key={field.key} className="border-b p-2 align-middle">
                                                        {tagInput(row)}
                                                    </td>
                                                ) : (
                                                    <td key={field.key} className={cn('max-w-[240px] truncate border-b p-2', field.mono && 'font-mono text-xs')} title={field.value(row)}>
                                                        {field.value(row)}
                                                    </td>
                                                ),
                                            )}
                                            <td className={cn('sticky right-0 z-10 border-b p-2 align-top', tone.sticky, STICKY_RIGHT_EDGE)}>
                                                <div className="w-[320px] space-y-1">
                                                    <StatusBadge row={row} />
                                                    <Messages row={row} />
                                                </div>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr>
                                                <td colSpan={SUMMARY_FIELDS.length + 2} className="border-b p-0">
                                                    <RowDetails row={row} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col gap-3 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-muted-foreground">
                        {importableCount} of {counts.total} assets can be imported.
                    </p>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={onConfirm} disabled={isImporting || importableCount === 0}>
                            {isImporting ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    <Upload className="mr-2 h-4 w-4" /> Confirm Import
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AssetImportPreview;
